import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import { date, money } from '../../utils/format';
import { ErrorNotice, Loading } from '../../components/ui';
import { Drawer } from '../../components/ui/Form';

interface JournalLine {
  line_no: number;
  code: string;
  name: string;
  debit: string;
  credit: string;
  narration: string | null;
}

interface Journal {
  id: string;
  journal_no: string;
  journal_date: string;
  journal_type: string;
  source_doc_type: string;
  narration: string | null;
  total_debit: string;
  total_credit: string;
  status: string;
  reversal_of_id: string | null;
  reversed_by_id: string | null;
  lines: JournalLine[];
}

/**
 * One entry, with both sides shown.
 *
 * Reversing never deletes: it posts an opposite entry that references this one,
 * and both stay readable afterwards.
 */
export function JournalDrawer({ journalId, onClose }: { journalId: string; onClose: () => void }) {
  const { can } = usePermission();
  const client = useQueryClient();
  const [error, setError] = useState<unknown>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['journal', journalId],
    queryFn: () => api.get<Journal>(`/journals/${journalId}`),
  });

  const reverse = useMutation({
    mutationFn: (reason: string) => api.post(`/journals/${journalId}/reverse`, { reason }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['journal'] });
      void client.invalidateQueries({ queryKey: ['ledger'] });
      void client.invalidateQueries({ queryKey: ['journals'] });
      onClose();
    },
    onError: setError,
  });

  return (
    <Drawer
      title={data ? `Voucher ${data.journal_no}` : 'Voucher'}
      subtitle={data ? `${data.journal_type.toLowerCase()} · ${date(data.journal_date)}` : undefined}
      onClose={onClose}
    >
      <ErrorNotice error={error} />
      {isLoading ? <Loading /> : null}

      {data ? (
        <>
          {data.narration ? <p className="mb-3 text-sm">{data.narration}</p> : null}

          {data.reversal_of_id ? (
            <p className="mb-3 rounded border border-ink/15 bg-white p-2 text-xs">
              This entry reverses an earlier one.
            </p>
          ) : null}
          {data.reversed_by_id ? (
            <p className="mb-3 rounded border border-danger/30 bg-danger/5 p-2 text-xs text-danger">
              This entry has been reversed. It stays on the record, as does the reversal.
            </p>
          ) : null}

          <table className="w-full text-sm">
            <thead>
              <tr className="border-y border-ink/20 text-xs uppercase tracking-wide text-ink-soft">
                <th className="py-1 text-left">Account</th>
                <th className="py-1 text-right">Debit</th>
                <th className="py-1 text-right">Credit</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((l) => (
                <tr key={l.line_no} className="border-b border-ink/10">
                  <td className="py-1">
                    <span className="num text-xs text-ink-soft">{l.code}</span> {l.name}
                    {l.narration ? <span className="block text-xs text-ink-soft">{l.narration}</span> : null}
                  </td>
                  <td className="num py-1 text-right">{Number(l.debit) > 0 ? money(l.debit) : ''}</td>
                  <td className="num py-1 text-right">{Number(l.credit) > 0 ? money(l.credit) : ''}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-ink font-semibold">
                <td className="py-1">Total</td>
                <td className="num py-1 text-right">{money(data.total_debit)}</td>
                <td className="num py-1 text-right">{money(data.total_credit)}</td>
              </tr>
            </tfoot>
          </table>

          {/* The invariant, visible rather than asserted in a comment. */}
          <p className="mt-2 text-xs text-success">
            {data.total_debit === data.total_credit ? 'Debits equal credits.' : 'This entry does not balance.'}
          </p>

          {can('accounts.reverse') && !data.reversed_by_id && data.status === 'POSTED' ? (
            <button
              className="btn-ghost mt-5"
              disabled={reverse.isPending}
              onClick={() => {
                const reason = window.prompt('Why is this being reversed? The reason is kept permanently.');
                if (reason && reason.trim().length >= 3) reverse.mutate(reason);
              }}
            >
              Reverse this entry
            </button>
          ) : null}
        </>
      ) : null}
    </Drawer>
  );
}
